import { useCallback, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import { AlertManager } from '@dealroadshow/uikit-alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { SortOrder } from '@dealroadshow/uikit-table/DataTable/interfaces/SortOrder';

interface IFetchCollectionInitialValues<T> {
  isInitialized?: boolean,
  isFetching?: boolean,
  isZeroCase?: boolean,
  collection?: T[],
  sortBy?: string,
  sortOrder?: SortOrder,
  page?: number,
  perPage?: number,
  debouncedSearchString?: string,
  totalCount?: number,
}

export interface IFetchCollectionResponse<T> {
  collection: T[],
  totalCount: number,
}

export interface IFetchCollectionPayload {
  [key: string]: {} | string | number | boolean | IFetchCollectionPayload | string[] | number[],
}

export interface IFetchCallback<T, K = IFetchCollectionPayload> {
  (payload: K): Promise<IFetchCollectionResponse<T>>,
}

export type CallbackCollectionType<C extends IFetchCallback<any>> = C extends IFetchCallback<infer T> ? T : unknown;

interface IUseFetchCollectionOptions<T, K> {
  payload?: K,
  initialValues?: IFetchCollectionInitialValues<T>,
  enabled?: boolean,
  onError?: (error: unknown) => void,
}

/**
 * Hook for managing paginated, sortable, searchable collections.
 *
 * @param fetch - Async fetcher returning `{ collection, totalCount }`.
 *   Accepts `null` for backward compat (same as `enabled: false`).
 * @param options.payload - Extra params merged into every fetch call. Refetches on change.
 * @param options.initialValues - Override defaults: sortBy, sortOrder, page, perPage, etc.
 * @param options.enabled - When `false`, prevents automatic fetching.
 * @param options.onError - Custom error handler. AbortErrors are always ignored.
 */
const useFetchCollection = <T, K = IFetchCollectionPayload>(
  fetch: IFetchCallback<T, K> | null | undefined,
  options: IUseFetchCollectionOptions<T, K> = {},
) => {
  const {
    payload = null,
    initialValues = {},
    enabled = true,
    onError = (error) => {
      AlertManager.error(getErrorMessage(error));
    },
  } = options;

  const defaultInitialValues: IFetchCollectionInitialValues<T> = {
    isInitialized: false,
    isFetching: false,
    isZeroCase: false,
    collection: [],
    sortBy: 'name',
    sortOrder: 'asc',
    page: 1,
    perPage: 25,
    debouncedSearchString: '',
    totalCount: 0,
  };

  const initial: IFetchCollectionInitialValues<T> = {
    ...defaultInitialValues,
    ...initialValues,
  };
  const [isPristine, setIsPristine] = useState<boolean>(true);
  // @ts-ignore TODO: FIN-32798 strictNullChecks
  const [isInitialized, setIsInitialized] = useState<boolean>(initial.isInitialized);
  // @ts-ignore TODO: FIN-32798 strictNullChecks
  const [isFetching, setIsFetching] = useState<boolean>(initial.isFetching);
  // @ts-ignore TODO: FIN-32798 strictNullChecks
  const [isZeroCase, setIsZeroCase] = useState<boolean>(initial.isZeroCase);
  // @ts-ignore TODO: FIN-32798 strictNullChecks
  const [collection, setCollection] = useState<T[]>(initial.collection);
  // @ts-ignore TODO: FIN-32798 strictNullChecks
  const [totalCount, setTotalCount] = useState<number>(initial.totalCount);

  const [params, setParams] = useState({
    sortBy: initial.sortBy,
    sortOrder: initial.sortOrder,
    page: initial.page,
    perPage: initial.perPage,
    debouncedSearchString: initial.debouncedSearchString,
  });

  const {
    sortBy,
    sortOrder,
    page,
    perPage,
    debouncedSearchString,
  } = params;

  const getCollection = async (): Promise<void> => {
    setIsFetching(true);

    try {
      // @ts-ignore TODO: FIN-32798 strictNullChecks
      const response = await fetch({
        sortBy,
        sortOrder,
        page,
        perPage,
        search: debouncedSearchString,
        ...payload,
      });

      // @ts-ignore TODO: FIN-32798 strictNullChecks
      if (!response.collection.length && page > 1) {
        // @ts-ignore TODO: FIN-32798 strictNullChecks
        paginate(initial.page);
      } else {
        setCollection(response.collection);
        setTotalCount(response.totalCount);
        setIsFetching(false);
        setIsInitialized(true);
        setIsZeroCase(isEmpty(response.collection) && !debouncedSearchString);
      }
    } catch (error) {
      if (Dispatcher.isAbortError(error)) return;
      onError(error);
      setCollection([]);
      setIsFetching(false);
    }
  };

  const reset = useCallback((): void => {
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    setIsInitialized(initial.isInitialized);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    setIsFetching(initial.isFetching);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    setCollection(initial.collection);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    setIsZeroCase(initial.isZeroCase);
    softReset();
  }, [initial]);

  const softReset = useCallback((): void => {
    setParams({
      sortBy: initial.sortBy,
      sortOrder: initial.sortOrder,
      page: initial.page,
      perPage: initial.perPage,
      debouncedSearchString: initial.debouncedSearchString,
    });
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    setTotalCount(initial.totalCount);
  }, [initial]);

  const sort = (sortBy: string, sortOrder: SortOrder): void => {
    setParams({
      ...params,
      page: 1,
      sortBy,
      sortOrder,
    });
  };

  const setItemsPerPage = (perPage: number): void => {
    setParams({
      ...params,
      page: 1,
      perPage,
    });
  };

  const paginate = (page: number): void => {
    setParams({
      ...params,
      page,
    });
  };

  const search = (debouncedSearchString: string): void => {
    setParams({
      ...params,
      debouncedSearchString,
    });
  };

  const debouncedSearch = useCallback(debounce((debouncedSearchString: string): void => {
    setParams({
      ...params,
      page: 1,
      debouncedSearchString,
    });
  }, 500), [setParams, params]);

  useEffect(() => {
    enabled && fetch && getCollection();
  }, [
    JSON.stringify(payload), // TODO replace JSON.stringify
    sortBy,
    sortOrder,
    page,
    perPage,
    debouncedSearchString,
    enabled,
    fetch,
  ]);

  useEffect(() => {
    setIsPristine(isEqual(params, {
      sortBy: initial.sortBy,
      sortOrder: initial.sortOrder,
      page: initial.page,
      perPage: initial.perPage,
      debouncedSearchString: initial.debouncedSearchString,
    }));
  }, [params]);

  return {
    isPristine,
    isInitialized,
    isFetching,
    isZeroCase,
    collection,
    sortBy,
    sortOrder,
    page,
    perPage,
    totalCount,
    debouncedSearchString,
    sort,
    setItemsPerPage,
    paginate,
    search,
    debouncedSearch,
    reset,
    softReset,
    getCollection,
    setIsFetching,
    modifyCollection: setCollection,
  };
};

export default useFetchCollection;
